import { Avatar, CircularProgress, IconButton, Stack, Typography } from '@mui/material';
import { useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ReactComponent as RobotIcon } from '../../assets/icons/chat-icons/robot.svg';
import { ReactComponent as MinimizeIcon } from '../../assets/icons/chat-icons/caret-down-fill.svg';
import CustomButton from '../../components/button/CustomButton';
import useAuth from '../../utils/hooks/useAuth';
import { useAiChatStore } from './useAiChatStore';
import { useAiChat } from './useAiChat';
import { useAiChatMinimizeStore } from './useAiChatMinimizeStore';
import { useFloatingChatStore } from './useFloatingChatStore';
import { APPBAR_HEIGHT } from '../../layout/dashboard-navigation/NavigationDashboard';
import CustomTooltip from '../../components/tooltip/CustomTooltip';
import { ChatInput } from './ChatInput';
import AiChatDisclaimer from './AiChatDisclaimer';

/**
 * AI Assistant Active Conversation View
 * Displays messages and input field for the active conversation
 */
export default () => {
  const {
    authData: { userData },
  } = useAuth();

  const { conversations, activeConversationId, isTyping, createAiConversation } = useAiChatStore();

  const { sendMessage: sendAiMessage } = useAiChat();
  const { setMinimized } = useAiChatMinimizeStore();
  const { setMode, setExpanded } = useFloatingChatStore();
  const navigate = useNavigate();

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  const activeConversation = conversations.find((c) => c.id === activeConversationId);

  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [activeConversation?.messages]);

  const sendMessage = async (content: string) => {
    let conversation = activeConversation;
    if (!conversation) {
      conversation = await createAiConversation();
    }

    await sendAiMessage(conversation.id, content);
  };

  const handleMinimize = () => {
    setMinimized(true);
    setMode('ai');
    setExpanded(true);
    navigate('/dashboard');
  };

  // If no conversation is active, show welcome screen
  if (!activeConversationId && conversations.length === 0) {
    return (
      <Stack
        flex={1}
        alignItems="center"
        justifyContent="center"
        height={`calc(100vh - ${APPBAR_HEIGHT}px)`}
        sx={{ backgroundColor: '#fff', overflow: 'hidden' }}
      >
        <Stack alignItems="center" gap={3} maxWidth="500px" px={4}>
          <RobotIcon width={80} height={80} fill="#6D51F6" />
          <Typography variant="h4" color="primaryDark.600" textAlign="center">
            Welcome to AI Assistant
          </Typography>
          <Typography variant="body1" color="primaryDark.400" textAlign="center">
            Get instant help with your projects, tasks, and Spona platform features. Start a new
            conversation to begin chatting with your AI assistant.
          </Typography>
          <CustomButton
            variant="primary"
            onClick={async () => {
              await createAiConversation();
            }}
          >
            Start New Conversation
          </CustomButton>
        </Stack>
      </Stack>
    );
  }

  // If no active conversation selected but conversations exist
  if (!activeConversationId) {
    return (
      <Stack
        flex={1}
        alignItems="center"
        justifyContent="center"
        height={`calc(100vh - ${APPBAR_HEIGHT}px)`}
        sx={{ backgroundColor: '#fff', overflow: 'hidden' }}
      >
        <Stack alignItems="center" gap={2} maxWidth="400px" px={4}>
          <RobotIcon width={64} height={64} fill="#6D51F6" />
          <Typography variant="h5" color="primaryDark.600" textAlign="center">
            Select a conversation
          </Typography>
          <Typography variant="body2" color="primaryDark.400" textAlign="center">
            Choose a conversation from the sidebar or create a new one to continue chatting.
          </Typography>
        </Stack>
      </Stack>
    );
  }

  // Active conversation view
  return (
    <Stack
      flex={1}
      height={`calc(100vh - ${APPBAR_HEIGHT}px)`}
      sx={{ backgroundColor: '#fff', overflow: 'hidden' }}
      direction="column"
    >
      {/* Header */}
      <Stack
        px={4}
        py={2}
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        borderBottom="1px solid"
        borderColor="secondaryBlue.100"
        sx={{ backgroundColor: '#fff', flexShrink: 0 }}
      >
        <Stack direction="row" gap={1.5} alignItems="center" flex={1}>
          <Avatar
            sx={{
              width: 40,
              height: 40,
              backgroundColor: 'secondaryBlue.500',
            }}
          >
            <RobotIcon width={24} height={24} fill="#fff" />
          </Avatar>
          <Stack>
            <Typography variant="heading3" color="primaryDark.600">
              {activeConversation?.title}
            </Typography>
            <Typography variant="body3" color="primaryDark.400" fontSize="12px">
              AI Assistant • {activeConversation?.messages.length} messages
            </Typography>
          </Stack>
        </Stack>
        <Stack direction="row" gap={1} alignItems="center" mr={6}>
          <CustomTooltip title="Open in floating chat" placement="bottom">
            <IconButton
              onClick={handleMinimize}
              sx={{
                backgroundColor: 'secondaryBlue.50',
                '&:hover': {
                  backgroundColor: 'secondaryBlue.100',
                },
              }}
            >
              <MinimizeIcon width={20} height={20} />
            </IconButton>
          </CustomTooltip>
        </Stack>
      </Stack>
      <AiChatDisclaimer />

      {/* Messages Area */}
      <Stack
        ref={messagesContainerRef}
        sx={{
          overflowY: 'auto',
          minHeight: 0,
          overscrollBehavior: 'contain',
          flex: 1,
          gap: 2,
          py: 3,
          px: 4,
        }}
      >
        {activeConversation?.messages.length === 0 ? (
          <Stack py={8} alignItems="center" gap={2}>
            <RobotIcon width={48} height={48} fill="#6D51F6" />
            <Typography variant="heading4" color="primaryDark.600" textAlign="center">
              How can I help you today?
            </Typography>
            <Typography variant="body3" color="primaryDark.400" textAlign="center">
              Ask me anything about your projects, tasks, or Spona platform features.
            </Typography>
          </Stack>
        ) : (
          <>
            {activeConversation?.messages.map((message) => (
              <Stack
                key={message.id}
                direction="row"
                gap={1.5}
                alignItems="flex-start"
                justifyContent={message.role === 'user' ? 'flex-end' : 'flex-start'}
              >
                {message.role === 'assistant' && (
                  <Avatar
                    sx={{
                      width: 32,
                      height: 32,
                      backgroundColor: 'secondaryBlue.500',
                    }}
                  >
                    <RobotIcon width={20} height={20} fill="#fff" />
                  </Avatar>
                )}
                <Stack
                  sx={{
                    maxWidth: '70%',
                    p: 2,
                    borderRadius: '12px',
                    backgroundColor:
                      message.role === 'user' ? 'secondaryBlue.500' : 'secondaryBlue.50',
                  }}
                >
                  <Typography
                    variant="body3"
                    color={message.role === 'user' ? '#fff' : 'primaryDark.600'}
                    sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                  >
                    {message.content}
                  </Typography>
                </Stack>
                {message.role === 'user' && (
                  <Avatar
                    src={userData?.avatar}
                    sx={{
                      width: 32,
                      height: 32,
                    }}
                  >
                    {userData?.firstName?.[0]}
                  </Avatar>
                )}
              </Stack>
            ))}
            {isTyping && (
              <Stack direction="row" gap={1.5} alignItems="flex-start">
                <Avatar
                  sx={{
                    width: 32,
                    height: 32,
                    backgroundColor: 'secondaryBlue.500',
                  }}
                >
                  <RobotIcon width={20} height={20} fill="#fff" />
                </Avatar>
                <Stack
                  sx={{
                    p: 2,
                    borderRadius: '12px',
                    backgroundColor: 'secondaryBlue.50',
                  }}
                >
                  <CircularProgress size={16} />
                </Stack>
              </Stack>
            )}
            <div ref={messagesEndRef} />
          </>
        )}
      </Stack>

      {/* Input Area */}
      <Stack
        px={4}
        py={3}
        borderTop="1px solid"
        borderColor="secondaryBlue.100"
        sx={{ backgroundColor: '#FAFAFA', flexShrink: 0 }}
      >
        <ChatInput onSendMessage={sendMessage} disabled={isTyping} />
      </Stack>
    </Stack>
  );
};
